// dashboard/views/EnrollmentsView.js
// The pending/rejected enrollments tab. Same reasoning as DashboardView —
// previously an inline `view === 'enrollments' && <JSX>}` block that never
// unmounted, so pendingPets/rejectedPets only ever reflected the single
// fetchAll() at login. Now a real component App() mounts only while
// `view === 'enrollments'`, with its own mount-time refetch. State
// ownership/fetch logic stays in App() via the onMount callback — the
// Sidebar's pending-count badge needs pendingPets regardless of which tab
// is active, and handleReject/handleReverseRejection/handleAssign are
// shared with PatientDetailPanel's "Assign Condition" action. Extracted/
// added as part of the App.js split (2026-08-12).

import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import i18n from '../../i18n';
import { c } from '../constants';
import { getAge, formatPhone } from '../utils';

export default function EnrollmentsView({
  onMount, loading,
  pendingError, pendingPets, canClinical, onConfirm, onReject,
  rejectedError, rejectedPets, rejectedExpanded, setRejectedExpanded, onReverseRejection,
  processingPetIds = new Set(),
}) {
  const { t } = useTranslation();

  useEffect(() => { onMount(); }, [onMount]);

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: c.slate, margin: '0 0 4px' }}>{t('mainView.enrollmentsTitle')}</h1>
        <p style={{ fontSize: 13, color: c.textSoft, margin: 0 }}>{t('mainView.enrollmentsSubtitle')}</p>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: c.textSoft }}>{t('common.loading')}</div>
      ) : pendingError ? (
        <div style={{ backgroundColor: '#fff5f5', borderRadius: 12, padding: 40, textAlign: 'center', color: c.error, border: `1px solid ${c.error}33` }}>
          <div style={{ marginBottom: 12 }}>{t('mainView.couldNotLoadPending')}</div>
          <button
            onClick={onMount}
            style={{ padding: '6px 12px', backgroundColor: c.white, color: c.slate, border: `1px solid ${c.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
          >
            {t('common.retry')}
          </button>
        </div>
      ) : pendingPets.length === 0 ? (
        <div style={{ backgroundColor: c.white, borderRadius: 12, padding: 40, textAlign: 'center', color: c.textSoft, border: `1px solid ${c.border}` }}>{t('mainView.noPendingEnrollments')}</div>
      ) : (
        <div style={{ backgroundColor: c.white, borderRadius: 12, overflow: 'hidden', border: `1px solid ${c.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: c.fog }}>
                {[t('mainView.colOwner'), t('mainView.colPet'), t('mainView.colSpecies'), t('mainView.colAge'), t('mainView.colContact'), t('mainView.colStatus'), t('mainView.colActions')].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: c.textSoft, borderBottom: `1px solid ${c.border}`, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pendingPets.map(pet => (
                <tr key={pet.id}>
                  {[`${pet.profiles?.first_name ?? ''} ${pet.profiles?.last_name ?? ''}`.trim() || '—', pet.name, pet.species, getAge(pet.date_of_birth), pet.profiles?.phone ? formatPhone(pet.profiles.phone) : pet.profiles?.email || '—'].map((v, i) => (
                    <td key={i} style={{ padding: '12px 16px', fontSize: 13, color: c.slate, borderBottom: `1px solid ${c.border}` }}>{v}</td>
                  ))}
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid ${c.border}` }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: c.yellow, backgroundColor: c.yellowLight, padding: '2px 8px', borderRadius: 10 }}>{t('mainView.statusPending')}</span>
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid ${c.border}` }}>
                    {canClinical ? (
                      <>
                        <button onClick={() => onConfirm(pet)} style={{ padding: '6px 12px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6 }}>{t('common.confirm')}</button>
                        <button onClick={() => onReject(pet)} disabled={processingPetIds.has(pet.id)} style={{ padding: '6px 12px', background: 'none', border: `1px solid ${c.border}`, borderRadius: 6, fontSize: 12, cursor: processingPetIds.has(pet.id) ? 'default' : 'pointer', color: c.textSoft, opacity: processingPetIds.has(pet.id) ? 0.5 : 1 }}>{t('common.reject')}</button>
                      </>
                    ) : (
                      <span style={{ fontSize: 11, color: c.textSoft }}>{t('mainView.doctorReviewRequired')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && rejectedError && (
        <div style={{ marginTop: 24, fontSize: 12, color: c.textSoft }}>
          {t('mainView.couldNotLoadRejected')}
        </div>
      )}

      {!loading && rejectedPets.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <button
            onClick={() => setRejectedExpanded(prev => !prev)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', color: c.textSoft, fontSize: 13, fontWeight: 600 }}
          >
            <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: rejectedExpanded ? 'rotate(90deg)' : 'rotate(0deg)', fontSize: 10 }}>▶</span>
            {t('mainView.rejectedInLast30Days', { count: rejectedPets.length })}
          </button>
          {rejectedExpanded && (
            <div style={{ backgroundColor: c.white, borderRadius: 12, overflow: 'hidden', border: `1px solid ${c.border}`, marginTop: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: c.fog }}>
                    {[t('mainView.colOwner'), t('mainView.colPet'), t('mainView.colSpecies'), t('mainView.colAge'), t('mainView.colContact'), t('mainView.colRejected'), t('mainView.colActions')].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: c.textSoft, borderBottom: `1px solid ${c.border}`, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rejectedPets.map(pet => (
                    <tr key={pet.id}>
                      {[
                        `${pet.profiles?.first_name ?? ''} ${pet.profiles?.last_name ?? ''}`.trim() || '—',
                        pet.name,
                        pet.species,
                        getAge(pet.date_of_birth),
                        pet.profiles?.phone ? formatPhone(pet.profiles.phone) : pet.profiles?.email || '—',
                        pet.rejectedAt ? new Date(pet.rejectedAt).toLocaleDateString(i18n.language) : '—',
                      ].map((v, i) => (
                        <td key={i} style={{ padding: '12px 16px', fontSize: 13, color: c.slate, borderBottom: `1px solid ${c.border}` }}>{v}</td>
                      ))}
                      <td style={{ padding: '12px 16px', borderBottom: `1px solid ${c.border}` }}>
                        {canClinical ? (
                          <button
                            onClick={() => onReverseRejection(pet)}
                            disabled={processingPetIds.has(pet.id)}
                            style={{ padding: '6px 12px', backgroundColor: c.white, border: `1px solid ${c.moss}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: processingPetIds.has(pet.id) ? 'default' : 'pointer', color: c.moss, opacity: processingPetIds.has(pet.id) ? 0.5 : 1 }}
                          >
                            {t('mainView.reverse')}
                          </button>
                        ) : (
                          <span style={{ fontSize: 11, color: c.textSoft }}>{t('mainView.doctorReviewRequired')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}
